import { Injectable } from '@angular/core'
import {
    Observable,
    of,
    tap,
    catchError,
    throwError,
    finalize,
    shareReplay,
    take,
    timeout,
    TimeoutError,
} from 'rxjs'
import { MarkerDimension } from '../../state/case/case.state'
import { DimensionDataWithDomain } from '../../state/plot-data/plot-data.state'
import { DatasetApiService } from './dataset-api.service'

const PLOT_DATA_LOG = '[PlotData]'
const FETCH_TIMEOUT_MS = 120_000

@Injectable({
    providedIn: 'root',
})
export class PlotDataCacheService {
    /** Enough for several maps × X/Y/color without evicting mid multi-map load. */
    private readonly maxEntries = 16
    private readonly cache = new Map<string, DimensionDataWithDomain>()
    private readonly accessOrder: string[] = []
    private readonly inFlight = new Map<string, Observable<DimensionDataWithDomain>>()

    constructor(private readonly datasetApi: DatasetApiService) {}

    getDataForDimension(
        marker: MarkerDimension,
        mapId?: number | string
    ): Observable<DimensionDataWithDomain> {
        const resolvedMapId = mapId ?? marker.segobjectpropertymap?.id ?? ''
        const key = `${resolvedMapId}:${marker.name}`

        if (this.cache.has(key)) {
            console.log(PLOT_DATA_LOG, 'cache hit', { key, marker: marker.name })
            this.touch(key)
            return of(this.cache.get(key)!)
        }

        const inFlightRequest = this.inFlight.get(key)
        if (inFlightRequest) {
            console.log(PLOT_DATA_LOG, 'joining in-flight fetch', {
                key,
                marker: marker.name,
                source_bin: marker.source_bin,
            })
            return inFlightRequest.pipe(take(1))
        }

        console.log(PLOT_DATA_LOG, 'cache miss → fetch started', {
            key,
            mapId: resolvedMapId,
            marker: marker.name,
            source_bin: marker.source_bin,
            source_json: marker.source_json,
        })

        const request$ = this.datasetApi.getDataForDimension(marker).pipe(
            timeout({ first: FETCH_TIMEOUT_MS }),
            tap(data => {
                console.log(PLOT_DATA_LOG, 'fetch completed', {
                    key,
                    marker: marker.name,
                    pointCount: data.points.length,
                })
                this.set(key, data)
            }),
            catchError(err => {
                if (err instanceof TimeoutError) {
                    console.error(PLOT_DATA_LOG, 'fetch timed out', {
                        key,
                        marker: marker.name,
                        source_bin: marker.source_bin,
                        timeoutMs: FETCH_TIMEOUT_MS,
                    })
                } else {
                    console.error(PLOT_DATA_LOG, 'fetch failed', {
                        key,
                        marker: marker.name,
                        source_bin: marker.source_bin,
                        err,
                    })
                }
                return throwError(() => err)
            }),
            shareReplay({ bufferSize: 1, refCount: true }),
            finalize(() => {
                this.inFlight.delete(key)
                console.log(PLOT_DATA_LOG, 'fetch finalized', { key, marker: marker.name })
            })
        )

        this.inFlight.set(key, request$)
        return request$.pipe(take(1))
    }

    clear(): void {
        const staleInFlight = this.inFlight.size
        console.log(PLOT_DATA_LOG, 'plot data cache cleared', { staleInFlightDropped: staleInFlight })
        this.cache.clear()
        this.accessOrder.length = 0
        this.inFlight.clear()
    }

    /** Drop stale in-flight handles so a new load does not join a dead observable. */
    clearInFlight(): void {
        if (this.inFlight.size > 0) {
            console.log(PLOT_DATA_LOG, 'in-flight requests cleared', { count: this.inFlight.size })
        }
        this.inFlight.clear()
    }

    private set(key: string, data: DimensionDataWithDomain): void {
        if (this.cache.has(key)) {
            this.touch(key)
            this.cache.set(key, data)
            return
        }

        while (this.cache.size >= this.maxEntries && this.accessOrder.length > 0) {
            const oldest = this.accessOrder.shift()!
            this.cache.delete(oldest)
        }

        this.cache.set(key, data)
        this.accessOrder.push(key)
    }

    private touch(key: string): void {
        const index = this.accessOrder.indexOf(key)
        if (index >= 0) {
            this.accessOrder.splice(index, 1)
        }
        this.accessOrder.push(key)
    }
}
